import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Empty, ErrorBanner, Pill, Tile, fmt } from "../components/ui";
import {
  api,
  type DialingBreakdown,
  type DialingCell,
  type DialingEverything,
  type DialingValues,
} from "../lib/api";

/** Seconds, as a person would say them. */
function secs(s: number | null | undefined) {
  if (s == null) return "—";
  if (s < 90) return `${Math.round(s)}s`;
  const m = s / 60;
  return m < 90 ? `${m.toFixed(1)}m` : `${(m / 60).toFixed(1)}h`;
}

function pct(v: number | null | undefined) {
  return v == null ? "—" : `${(v * 100).toFixed(1)}%`;
}

/** Today's date in Eastern, as YYYY-MM-DD.
 *
 *  Eastern and not the browser's timezone: every number on this page is bucketed by
 *  `five9_dials.call_date_local`, which is Eastern, so a viewer in another zone asking for
 *  "today" must still get the Eastern today or the totals will not match the tiles.
 *
 *  `en-CA` formats as YYYY-MM-DD, so the calendar date is read straight out of the formatter in
 *  the zone asked for. The previous version round-tripped through a Date:
 *
 *      new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }))
 *        .toISOString().slice(0, 10)
 *
 *  which converts TWICE - it renders the Eastern wall clock, re-reads that as a time in the
 *  BROWSER's zone, then converts it to UTC. In Eastern itself every hour from 20:00 came back as
 *  tomorrow, so "Today" asked for a day with no dials and the two date boxes showed dates nobody
 *  had picked. The error depends on the viewer's offset and the hour, which is why it looked
 *  intermittent. */
function etToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

/** The last N Eastern days as a from/to pair, inclusive of today. */
function lastDays(n: number) {
  const until = etToday();
  // Arithmetic on the DATE, at UTC noon, so a DST change cannot move the day: "2026-10-07" plus
  // 12 hours minus 13 days is still inside 2026-09-24 whichever way the clocks went.
  const start = new Date(Date.parse(`${until}T12:00:00Z`) - (n - 1) * 86_400_000);
  return { since: start.toISOString().slice(0, 10), until };
}

const RANGES: { label: string; days: number }[] = [
  { label: "Today", days: 1 },
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
];

/** What "connected" means, in seconds. Ammar, 2026-09-30: "connected call is more than 2mins
 *  or 2 mins" - so 2:00 is the default. It is a CONTROL rather than a constant because the
 *  answer is genuinely sensitive to it: over 14 days of production dials the same range gives
 *  6,585 connected at 0s, 902 at 30s, 558 at 2m and 237 at 5m. A rate quoted without its
 *  threshold means nothing, which is why the chosen one is printed beside every rate below. */
const THRESHOLDS = [0, 30, 60, 120, 180, 300];

/** A dimension's name as a person would say it. Anything not named here is shown as it
 *  arrives - the list is a courtesy, not a whitelist, so a dimension added to the backend
 *  appears in the dropdowns immediately instead of waiting for a console deploy. */
const LABELS: Record<string, string> = {
  day: "day",
  week: "week",
  month: "month",
  hour_of_day: "hour of day (ET)",
  disposition: "disposition",
  outcome: "outcome",
  agent: "agent",
  direction: "direction",
  source: "where the record came from",
  our_number: "our caller ID",
  campaign: "campaign",
  role: "concierge vs closers (Five9 agent groups)",
  agent_raw: "agent, as the record spells it",
  routing: "routing we intended (our dials only)",
  list: "list",
  // NOT "call now" - that is a `reason`, and it spans BOTH entry types. Naming it here is what
  // made the entry_type table read as a call-now breakdown when it splits call-nows in half.
  entry_type: "how they entered (wants call / shoved / appointment)",
  reason: "why we called them (call_now, concierge_backfill, …)",
  phase: "reply day vs redial day",
  dial_no: "which dial",
  policy: "policy",
};

const label = (d: string) => LABELS[d] ?? d.replace(/_/g, " ");

/** `five9_dials.source` as a person would say it. An unknown source shows its raw name rather
 *  than being dropped - the reason this list exists is that a third feed (`call_log`) was wired
 *  while the page knew only two, and its 2,985 dials appeared in no row of the attribution card
 *  at all. */
const FEEDS: Record<string, string> = {
  webhook: "the live disposition webhook",
  call_log: "the Five9 Call Log report",
  bigquery_calls: "the warehouse (BigQuery)",
};

/** The comparisons worth one click, named the way the question gets asked. */
const PRESETS: { label: string; group_by: string[]; filters?: Record<string, string> }[] = [
  // `role` FIRST, and `routing` demoted. Ammar, 2026-09-30: "the numbers seems off and weird
  // as it says now ours,etc" - `routing` is what OUR engine intended and it had placed no
  // dials, so it read "(not ours)" for all 6,587. `role` comes from Five9's own agent groups
  // and applies to every dial with an agent on it.
  { label: "Concierge vs closers", group_by: ["role"] },
  { label: "Closers, by agent", group_by: ["agent"], filters: { role: "closers" } },
  { label: "Role × day", group_by: ["day", "role"] },
  { label: "Role × disposition", group_by: ["role", "disposition"] },
  { label: "Routing we intended (ours only)", group_by: ["routing"] },
  // `reason`, NOT `entry_type`. The preset here used to be labelled "Call now vs wants call vs
  // shoved" and grouped by `entry_type` - which has no `call_now` value at all. A call-now is a
  // REASON, and it arrives under two different entry types: on 2026-10-06, 83 call-now plans were
  // `wants_call` and 86 were `shoved`. So the row a reader took for "call nows" showed 162 dials
  // and 7 connected, while the real figure was 342 and 20 - the other half sitting inside the
  // `shoved` row mixed in with cold leads. The label promised a split the dimension cannot make.
  { label: "Why we called them (call now, backfill, takeover…)", group_by: ["reason"] },
  { label: "Call now, by list", group_by: ["list"], filters: { reason: "call_now" } },
  { label: "Entry type (wants call vs shoved)", group_by: ["entry_type"] },
  { label: "Entry type × list", group_by: ["entry_type", "list"] },
  { label: "Best hour to dial", group_by: ["hour_of_day"] },
  { label: "Agents", group_by: ["agent"] },
  { label: "Agents on connected calls", group_by: ["agent"], filters: { outcome: "spoke" } },
  { label: "Reply day vs redial day", group_by: ["phase"] },
  { label: "Which dial converts", group_by: ["dial_no"] },
  { label: "Disposition × routing", group_by: ["disposition", "routing"] },
  { label: "Day × concierge/closers", group_by: ["day", "routing"] },
  { label: "Caller ID", group_by: ["our_number"] },
  { label: "Inbound vs outbound", group_by: ["direction"] },
];

/** Columns of the pivot, in the order a reader wants them. */
const METRICS: { key: keyof DialingCell & string; head: string; kind: "n" | "pct" | "secs" }[] = [
  { key: "dials", head: "dials", kind: "n" },
  { key: "people", head: "people", kind: "n" },
  { key: "connected", head: "connected", kind: "n" },
  { key: "connect_rate", head: "conn rate", kind: "pct" },
  { key: "answered", head: "answered", kind: "n" },
  { key: "answer_rate", head: "ans rate", kind: "pct" },
  { key: "avg_seconds", head: "avg talk", kind: "secs" },
  { key: "talk_minutes", head: "talk", kind: "n" },
  { key: "appointments", head: "appts", kind: "n" },
  { key: "sales", head: "sales", kind: "n" },
  { key: "transfers", head: "xfers", kind: "n" },
  { key: "inbound", head: "inbound", kind: "n" },
];

function metric(cell: DialingCell, key: string, kind: "n" | "pct" | "secs") {
  const v = cell[key];
  if (v == null) return "—";
  if (kind === "pct") return pct(Number(v));
  if (kind === "secs") return secs(Number(v));
  return fmt.n(Number(v));
}

/** A small bar, so a day-by-day series reads at a glance without a chart library. */
function Bar({ value, of }: { value: number; of: number }) {
  const w = of > 0 ? Math.max(1, Math.round((value / of) * 100)) : 0;
  return (
    <div style={{ background: "var(--line)", borderRadius: 3, height: 8, width: 90 }}>
      <div style={{ background: "var(--ac)", borderRadius: 3, height: 8, width: `${w}%` }} />
    </div>
  );
}

export function Dialing() {
  // The range is two dates, not a preset index. The presets SET them, so "7 days" and a
  // hand-picked fortnight in August are the same kind of thing to everything downstream.
  const [range, setRange] = useState(() => lastDays(14));
  const [seconds, setSeconds] = useState(120);
  const [d, setD] = useState<DialingEverything | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ── the pivot ──────────────────────────────────────────────
  // `role`, not `routing`. The PRESETS below were reordered on 2026-09-30 to put `role` first
  // and demote `routing` - `role` comes from Five9's own agent groups and applies to every dial,
  // `routing` is what OUR engine intended and only exists for the dials it placed - but the
  // default the page actually opened on was never changed, so the first table every reader met
  // was the one the comment says not to lead with.
  const [groupBy, setGroupBy] = useState<string[]>(["role"]);
  const [filters, setFilters] = useState<{ key: string; value: string }[]>([]);
  const [bd, setBd] = useState<DialingBreakdown | null>(null);
  const [bdLoading, setBdLoading] = useState(false);
  const [dims, setDims] = useState<string[]>([]);
  const [oursOnly, setOursOnly] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>("dials");
  /** THREE-STATE, and `undefined` is the normal state: "I have not said, decide for me". The
   *  server turns it on when a cadence dimension is grouped. A plain `false` here is what made
   *  the page's first table one `(not ours)` row holding 93% of the dials, and a plain `true`
   *  would silently drop everybody else's dials from a breakdown by day. */
  const [onlyOurDials, setOnlyOurDials] = useState<boolean | undefined>(undefined);

  /** Monotonic request id PER ENDPOINT - one counter each, not one shared.
   *
   *  THE fix for "when i select date ranges numbers do not changes". There was no sequencing, and
   *  the response times are wildly uneven: measured on production 2026-10-07, a 14-day breakdown
   *  took 6.0s and a one-day one 0.7s. So clicking "14 days" and then "Today" fired A then B, B
   *  came back in under a second, and A landed five seconds later and overwrote it - leaving the
   *  fortnight's numbers under a control that said Today. The query is ~40x faster since the
   *  agent-role join was fixed, which makes the race rarer and not gone; rare and silent is the
   *  worst version of this bug, so the ordering is enforced rather than outrun.
   *
   *  Separate counters because the two loaders always fire together: one shared counter would
   *  have the breakdown's request number invalidate the overview's own in-flight reply, and the
   *  tiles would simply stop updating - the same symptom, caused by the fix for it. */
  const overviewReq = useRef(0);
  const breakdownReq = useRef(0);

  const load = useCallback(() => {
    const reqId = overviewReq;
    const mine = ++reqId.current;
    setLoading(true);
    api
      .dialing({ ...range, connected_seconds: seconds })
      .then((r) => {
        if (mine !== reqId.current) return;   // a newer request is in flight; this is stale
        setD(r);
        setError(null);
      })
      .catch((e) => {
        if (mine === reqId.current) setError(e.message);
      })
      .finally(() => {
        if (mine === reqId.current) setLoading(false);
      });
  }, [range, seconds]);

  const active = useMemo(
    () => Object.fromEntries(filters.filter((f) => f.key && f.value).map((f) => [f.key, f.value])),
    [filters],
  );

  const loadBreakdown = useCallback(() => {
    const reqId = breakdownReq;
    const mine = ++reqId.current;
    setBdLoading(true);
    api
      .dialingBreakdown({
        ...range,
        group_by: groupBy.filter(Boolean),
        filters: active,
        connected_seconds: seconds,
        ours_only: onlyOurDials,
      })
      .then((r) => {
        if (mine !== reqId.current) return;
        setBd(r);
      })
      .catch((e) => {
        if (mine === reqId.current) setError(e.message);
      })
      .finally(() => {
        if (mine === reqId.current) setBdLoading(false);
      });
  }, [range, groupBy, active, seconds, onlyOurDials]);

  // Fetched rather than hard-coded, so the dropdowns cannot offer a dimension the backend
  // does not accept - or miss one it does.
  useEffect(() => {
    api
      .dialingDimensions()
      .then((r) => {
        setDims(r.dimensions);
        setOursOnly(r.only_for_our_dials);
      })
      .catch(() => {});
  }, []);

  // Near-real-time: the endpoints read live tables, so re-asking is the whole mechanism. 60s
  // rather than 30 because this is eight queries, and paused while the tab is hidden.
  useEffect(() => {
    load();
    loadBreakdown();
    const t = setInterval(() => {
      if (!document.hidden) {
        load();
        loadBreakdown();
      }
    }, 60_000);
    return () => clearInterval(t);
  }, [load, loadBreakdown]);

  const o = d?.overview;
  const maxDials = Math.max(1, ...(o?.by_day ?? []).map((r) => r.dials));
  // Which preset, if any, this range IS - compared on both dates rather than on their distance
  // apart. Comparing the span alone lit "7 days" up for a hand-picked week in September, so the
  // control claimed a range the numbers were not for.
  const presetRange = RANGES.find((r) => {
    const l = lastDays(r.days);
    return l.since === range.since && l.until === range.until;
  });

  const rows = useMemo(() => {
    if (!bd) return [];
    const r = [...bd.rows];
    // Sorting client-side: the server already ordered by volume and capped the rows, so this
    // re-orders what is on screen rather than pretending to re-query.
    r.sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      return typeof av === "number" && typeof bv === "number"
        ? bv - av
        : String(av).localeCompare(String(bv));
    });
    return r;
  }, [bd, sortBy]);

  const notOurs = groupBy.some((g) => oursOnly.includes(g));

  return (
    <>
      <h1>Dialing</h1>
      <ErrorBanner error={error} />

      {/* Every control that changes what the numbers MEAN sits in one row at the top, so a
          reader never has to wonder which range or threshold a figure below came from. */}
      <div
        className="row"
        style={{ gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <div className="seg-ctl">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={presetRange?.days === r.days ? "on" : ""}
              onClick={() => setRange(lastDays(r.days))}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* NO `max={range.until}` on `from` and no `min={range.since}` on `to`.
            They made half the ranges unreachable: starting from the default fortnight, picking
            any day in September in the `to` box was impossible because the picker greyed out
            everything before `since`, so the only way to look at an earlier window was to guess
            the right order to click the two boxes in. Both boxes now go up to today and no
            further, and a reversed pair is simply swapped - `_range()` in the backend has always
            done that, so there was never a reason to prevent it in the UI.

            An empty value is IGNORED rather than stored. A cleared date box sends no `since`,
            the endpoint falls back to its own default fortnight, and the page then shows 14 days
            of numbers under two boxes that do not say 14 days - the exact shape of "i select date
            ranges and numbers do not change". */}
        <div>
          <div className="mut" style={{ fontSize: 11, marginBottom: 2 }}>from (Eastern)</div>
          <input
            className="f"
            type="date"
            value={range.since}
            max={etToday()}
            onChange={(e) =>
              e.target.value && setRange((r) => ({ ...r, since: e.target.value }))
            }
          />
        </div>
        <div>
          <div className="mut" style={{ fontSize: 11, marginBottom: 2 }}>to (inclusive)</div>
          <input
            className="f"
            type="date"
            value={range.until}
            max={etToday()}
            onChange={(e) =>
              e.target.value && setRange((r) => ({ ...r, until: e.target.value }))
            }
          />
        </div>

        <div>
          <div className="mut" style={{ fontSize: 11, marginBottom: 2 }}>
            a connected call lasts at least
          </div>
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <div className="seg-ctl">
              {THRESHOLDS.map((t) => (
                <button key={t} className={seconds === t ? "on" : ""} onClick={() => setSeconds(t)}>
                  {t === 0 ? "any" : t < 60 ? `${t}s` : `${t / 60}m`}
                </button>
              ))}
            </div>
            <input
              className="f"
              type="number"
              min={0}
              max={7200}
              step={10}
              value={seconds}
              style={{ width: 82 }}
              onChange={(e) => setSeconds(Math.max(0, Math.min(7200, Number(e.target.value) || 0)))}
            />
            <span className="mut" style={{ fontSize: 12 }}>sec</span>
          </div>
        </div>

        <span className="mut" style={{ fontSize: 12, paddingBottom: 6 }}>
          {o ? `${o.from} → ${o.to}` : ""} {loading ? "· refreshing…" : ""}
        </span>
      </div>

      {/* THE QUEUE, first and before any history. Ammar, 2026-09-30: "i don't see how many
          will be redialed,etc in there" - every other section answers "what happened", and at
          10am the question is "what is about to happen". Not date-ranged, so changing the
          range above does not blank it. */}
      {/* THE QUEUE. Ammar, 2026-09-30: "it says for today 900+ already dialed but we know
          those are not dialed but added in five9 list". He was right - `added` means handed to
          Five9, whose dialer is gated on agent availability, so the gap between handed over and
          actually called is hours wide. Every tile below names which of the three states it is. */}
      {d?.queue && (
        <Card
          title={
            <>
              Our redial engine{" "}
              {d.queue.engine_live ? (
                <Pill tone="ok">live</Pill>
              ) : (
                <Pill tone="ba">not live</Pill>
              )}{" "}
              {/* IN THE TITLE, not in a muted line under the table. This card is the first thing
                  on the page and none of its six tiles move when the date range above changes -
                  by design, it is the live queue - so it is the most likely single reason for
                  "when i select date ranges numbers do not changes". The note existed; it was
                  12px grey text below two tables, which is not where somebody checking whether a
                  control worked is looking. */}
              <Pill tone="ba">live now · ignores the dates above</Pill>
            </>
          }
        >
          <div className="tiles">
            <Tile
              label="still to be dialled"
              value={fmt.n(d.queue.still_to_be_dialled)}
              detail={`${fmt.n(d.queue.people_still_coming)} people`}
              tone={d.queue.still_to_be_dialled > 0 ? "ok" : undefined}
            />
            <Tile
              label="in the Five9 list, not yet called"
              value={fmt.n(d.queue.handed_to_five9_awaiting_dial)}
              detail="waiting for a free agent"
            />
            <Tile
              label="queued with us, not sent yet"
              value={fmt.n(d.queue.waiting_with_us)}
            />
            <Tile
              label="CALLS actually made today"
              value={fmt.n(d.queue.dialled_and_confirmed_today)}
              detail={`of ${fmt.n(d.queue.handed_to_five9_today)} handed to Five9`}
            />
            <Tile
              label="people in cadence"
              value={fmt.n(d.queue.people_in_cadence)}
              detail={d.queue.daily_cap == null ? "no daily cap" : `cap ${d.queue.daily_cap}`}
            />
            <Tile
              label="removals unverified"
              value={fmt.n(d.queue.removal_unverified)}
              tone={d.queue.removal_unverified > 0 ? "ba" : undefined}
              detail={d.queue.removal_unverified > 0 ? "may still be reachable by Five9" : "clean"}
            />
          </div>

          <div className="mut" style={{ fontSize: 12, marginTop: 8 }}>
            <b>“In the Five9 list” is not “called”.</b> We hand a record to Five9 and its dialer
            reaches it when an agent is free, which can be hours later. Only{" "}
            <b>CALLS actually made</b> counts calls that happened.
          </div>

          {d.queue.by_hour.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <b style={{ fontSize: 13 }}>Still queued with us, by hour (Eastern)</b>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>hour</th>
                    <th>to send</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.queue.by_hour.map((h) => (
                    <tr key={h.hour}>
                      <td>{h.hour}:00</td>
                      <td>{fmt.n(h.dials)}</td>
                      <td>
                        <Bar
                          value={h.dials}
                          of={Math.max(1, ...d.queue.by_hour.map((x) => x.dials))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <span className="mut" style={{ fontSize: 12 }}>
            {d.queue.by_list.map((l) => `${l.list_name}: ${l.dials}`).join(" · ")}
            {" — this is the live queue and ignores the date range above."}
          </span>
        </Card>
      )}

      {o && (
        <>
          {/* Two connect rates, side by side and both labelled, because they disagree by
              about a factor of two and a single unlabelled "connect rate" is the number
              somebody would quote in a meeting without knowing which one it was.

              EVERY tile here comes from `overview` - one population, every Vaunt dial in the
              range. "connected" and "answered" used to be read off the breakdown's totals, which
              carry the Explore panel's filters and its ours-only switch, so the row could read
              "16,557 dials · 32 connected" and offer 0.2% as the connect rate. The breakdown's
              own totals now live inside the Explore card, next to the controls that shaped
              them. */}
          <div className="tiles">
            <Tile label="dials" value={fmt.n(o.dials)} />
            <Tile label="people reached for" value={fmt.n(o.people)} />
            <Tile
              label={`connected (${seconds === 0 ? "any length" : seconds < 60 ? `${seconds}s+` : `${seconds / 60}m+`})`}
              value={fmt.n(o.connected_at_threshold ?? 0)}
              detail={
                o.dials
                  ? `${pct((o.connected_at_threshold ?? 0) / o.dials)} of all dials` +
                    // Named, because otherwise this tile cannot be made to agree with `dials`
                    // even at a threshold of "any length": 3,664 dials in the fortnight hold no
                    // duration, so they fail every threshold including zero. A reader comparing
                    // the two tiles has no other way to account for the gap.
                    (o.duration_unknown
                      ? ` · ${fmt.n(o.duration_unknown)} have no duration recorded`
                      : "")
                  : undefined
              }
            />
            <Tile
              label="answered (an agent took it)"
              value={fmt.n(o.dials_with_an_agent)}
              detail={o.dials ? `${pct(o.dials_with_an_agent / o.dials)} of all dials` : undefined}
            />
            <Tile label="talk time" value={`${fmt.n(Math.round(o.talk_minutes))}m`} />
            <Tile label="avg per connect" value={secs(o.avg_talk_seconds)} />
          </div>

          <ExploreCard
            bd={bd}
            bdLoading={bdLoading}
            rows={rows}
            dims={dims}
            oursOnly={oursOnly}
            notOurs={notOurs}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            filters={filters}
            setFilters={setFilters}
            sortBy={sortBy}
            setSortBy={setSortBy}
            seconds={seconds}
            range={range}
            onlyOurDials={onlyOurDials}
            setOnlyOurDials={setOnlyOurDials}
          />

          <div className="two">
            <Card title="By day">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>day</th>
                    <th>dials</th>
                    <th />
                    <th>reached a human</th>
                    <th>rate</th>
                  </tr>
                </thead>
                <tbody>
                  {o.by_day.map((r) => (
                    <tr key={r.day}>
                      <td>{r.day}</td>
                      <td>{fmt.n(r.dials)}</td>
                      <td><Bar value={r.dials} of={maxDials} /></td>
                      <td>{fmt.n(r.reached_a_human)}</td>
                      <td>{r.dials ? pct(r.reached_a_human / r.dials) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!o.by_day.length && <Empty>No dials in this range.</Empty>}
            </Card>

            <Card title="What the record can tell you">
              {/* Deliberately prominent. A dimension that is unavailable must not read as a
                  zero, because a zero means "it happened none times". */}
              <Row label="resolved to a person" value={`${d.attribution.resolved_to_a_person_pct}%`} />
              <Row label="dials we could not tie to a person" value={fmt.n(d.attribution.dials_not_resolved_to_a_person ?? d.attribution.unresolved)} />
              <Row label="have an agent recorded" value={`${d.attribution.with_an_agent_pct}%`} />
              {/* EVERY feed, from `by_feed`, rather than the two this card used to name. A third
                  ingest (`call_log`) was added and nothing here knew: the card said "webhook
                  8,852" and "warehouse 4,720" under a headline of 16,557 dials, so 2,985 dials
                  came from a source the page could not name - on the one card whose whole job is
                  saying where the record comes from. Listing them from the payload means the next
                  feed appears here the day it is wired, not the day somebody notices. */}
              {(d.attribution.by_feed ?? []).map((f) => (
                <Row
                  key={f.source}
                  label={`from ${FEEDS[f.source] ?? f.source}`}
                  value={fmt.n(f.dials)}
                />
              ))}
              {!d.attribution.by_feed && (
                <>
                  <Row label="from the live webhook" value={fmt.n(d.attribution.from_the_webhook)} />
                  <Row label="from the warehouse" value={fmt.n(d.attribution.from_bigquery)} />
                </>
              )}
              {d.attribution.campaign && (
                <Row
                  label="know which campaign dialled them"
                  value={
                    d.attribution.campaign.known_pct == null
                      ? "—"
                      : `${d.attribution.campaign.known_pct}%`
                  }
                />
              )}
              {d.attribution.agents_not_in_a_vaunt_group?.available &&
                d.attribution.agents_not_in_a_vaunt_group.dials > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <Pill tone="ba">
                      {fmt.n(d.attribution.agents_not_in_a_vaunt_group.dials)} dials have no role
                    </Pill>
                    <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>
                      {d.attribution.agents_not_in_a_vaunt_group.agents
                        .slice(0, 4)
                        .map((a) => `${a.agent} (${a.dials})`)
                        .join(", ")}
                      {d.attribution.agents_not_in_a_vaunt_group.agents.length > 4 && " …"}
                      {" — "}
                      {d.attribution.agents_not_in_a_vaunt_group.note}
                    </div>
                  </div>
                )}

              {/* A FEED OUTAGE IS NOT AN ATTRIBUTION GAP, and conflating the two is itself a
                  data-integrity bug. Only the call-log ingest carries `Call.campaign_name`; it
                  broke on 2026-10-06 at 14:04 ET and did not come back until early the next day.
                  On a day in this list the campaign is UNAVAILABLE, so a breakdown by campaign,
                  role or routing over it measures the feed rather than the dialling - which is
                  different from "these dials had no campaign". */}
              {!!d.attribution.campaign?.days_with_no_campaign_at_all?.length && (
                <div style={{ marginTop: 10 }}>
                  <Pill tone="ba">
                    {d.attribution.campaign.days_with_no_campaign_at_all.length} day(s) with no
                    campaign feed at all
                  </Pill>
                  <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>
                    {d.attribution.campaign.days_with_no_campaign_at_all.join(", ")} — don't group
                    by campaign over these days: the campaign is unavailable, not unattributed.
                  </div>
                </div>
              )}

              {/* What the Vaunt scope threw away, named. The scope keeps a dial only if its
                  campaign is unknown or begins "Vaunt", which is complete in a way the old
                  AmpaClaw/Aria deny-list was not - it had already missed GenPro. The price of
                  completeness is that a Vaunt campaign named something else would vanish, and "we
                  made fewer calls" is the error nobody questions, so the excluded names are on
                  screen. */}
              {!!d.attribution.other_clients_excluded?.dials && (
                <div style={{ marginTop: 10 }}>
                  <Pill tone="ba">
                    {fmt.n(d.attribution.other_clients_excluded.dials)} dials excluded as another
                    client's
                  </Pill>
                  <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>
                    {d.attribution.other_clients_excluded.campaigns
                      .map((c) => `${c.campaign} (${fmt.n(c.dials)})`)
                      .join(", ")}
                    {" — "}
                    {d.attribution.other_clients_excluded.note}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                {Object.entries(d.attribution.unavailable).map(([k, why]) => (
                  <div key={k} style={{ marginBottom: 6 }}>
                    <Pill tone="ba">{k} unavailable</Pill>
                    <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>{why}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <Card title="Concierge vs closers — outcomes for dials this engine placed">
            {d.routing.routing.length ? (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>routed to</th>
                    <th>dials</th>
                    <th>people</th>
                    <th>reached a human</th>
                    <th>rate</th>
                    <th>avg talk</th>
                    <th>appts</th>
                    <th>sales</th>
                  </tr>
                </thead>
                <tbody>
                  {d.routing.routing.map((r) => (
                    <tr key={r.routed_to}>
                      <td><b>{r.routed_to}</b></td>
                      <td>{fmt.n(r.dials)}</td>
                      <td>{fmt.n(r.people)}</td>
                      <td>{fmt.n(r.reached_a_human)}</td>
                      <td>{pct(r.reached_rate)}</td>
                      <td>{secs(r.avg_seconds)}</td>
                      <td>{fmt.n(r.appointments)}</td>
                      <td>{fmt.n(r.sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Empty>
                Nothing yet — this table only covers dials the engine placed, and it has not
                placed any. TextersAI and the appointment automation do not record which group
                they intended to reach, so their dials cannot appear here.
              </Empty>
            )}
            <span className="mut" style={{ fontSize: 12 }}>{d.routing.note}</span>
          </Card>

          <div className="two">
            <Card title="Agents (connected calls only)">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>agent</th>
                    <th>calls</th>
                    <th>talk</th>
                    <th>avg</th>
                    <th>appts</th>
                    <th>sales</th>
                  </tr>
                </thead>
                <tbody>
                  {d.agents.slice(0, 25).map((a) => (
                    <tr key={a.agent}>
                      <td>{a.agent}</td>
                      <td>{fmt.n(a.dials ?? a.calls)}</td>
                      <td>{a.talk_minutes == null ? "—" : `${fmt.n(Math.round(a.talk_minutes))}m`}</td>
                      <td>{secs(a.avg_seconds)}</td>
                      <td>{fmt.n(a.appointments)}</td>
                      <td>{fmt.n(a.sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.agents.length && <Empty>No connected calls in this range.</Empty>}
              <span className="mut" style={{ fontSize: 12 }}>
                An unanswered dial has no agent, so this is a table of conversations. Five9
                reports an agent as an email on some rows and a display name on others; they are
                shown as they arrive rather than merged, because merging could combine two people.
              </span>
            </Card>

            <Card title="Dispositions">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>disposition</th>
                    <th>dials</th>
                    <th>agent</th>
                    <th>avg</th>
                  </tr>
                </thead>
                <tbody>
                  {d.dispositions.slice(0, 20).map((r) => (
                    <tr key={`${r.disposition}-${r.outcome}`}>
                      <td>
                        {r.disposition}
                        <span className="mut" style={{ fontSize: 11 }}> · {r.outcome}</span>
                      </td>
                      <td>{fmt.n(r.dials)}</td>
                      <td>{fmt.n(r.with_agent)}</td>
                      <td>{secs(r.avg_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <div className="two">
            <Card title="Our cadence">
              <Row label="attempts" value={fmt.n(d.engine.attempts.attempts ?? 0)} />
              <Row label="awaiting an outcome" value={fmt.n(d.engine.attempts.awaiting_outcome ?? 0)} />
              <Row
                label="removal unverified"
                value={fmt.n(d.engine.attempts.removal_unverified ?? 0)}
              />
              <Row label="already in the list (603)" value={fmt.n(d.engine.attempts.already_present ?? 0)} />
              <Row label="reply-day dials" value={fmt.n(d.engine.attempts.reply_day ?? 0)} />
              <Row label="redial-day dials" value={fmt.n(d.engine.attempts.redial_day ?? 0)} />
              <hr style={{ border: 0, borderTop: "1px solid var(--line)", margin: "8px 0" }} />
              <Row
                label="redials of leads added the same day"
                value={fmt.n(d.engine.redials_of_leads_added_the_same_day)}
              />
              <Row
                label="redials of leads added earlier"
                value={fmt.n(d.engine.redials_of_leads_added_earlier)}
              />
              {d.engine.entries.length > 0 && (
                <table className="tbl" style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      <th>entered as</th>
                      <th>why</th>
                      <th>plans</th>
                      <th>active</th>
                      <th>stopped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.engine.entries.map((e) => (
                      <tr key={`${e.entry_type}-${e.reason}`}>
                        <td>{e.entry_type}</td>
                        <td>{e.reason}</td>
                        <td>{fmt.n(e.plans)}</td>
                        <td>{fmt.n(e.active)}</td>
                        <td>{fmt.n(e.stopped)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <Card title="Lists and list operations">
              {d.lists.lists.length ? (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>list</th>
                      <th>adds</th>
                      <th>Five9 accepted</th>
                      <th>already in list (603)</th>
                      <th>CALLS made</th>
                      <th>unverified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lists.lists.map((l) => (
                      <tr key={l.list_name}>
                        <td>{l.list_name}</td>
                        <td>{fmt.n(l.adds)}</td>
                        <td>{fmt.n(l.adds_accepted)}</td>
                        <td>{fmt.n(l.already_present)}</td>
                        <td><b>{fmt.n(l.dials_matched)}</b></td>
                        <td>{fmt.n(l.removal_unverified)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Empty>No adds from the engine in this range.</Empty>
              )}
              <span className="mut" style={{ fontSize: 12 }}>{d.lists.note}</span>

              <div style={{ marginTop: 12 }}>
                <b style={{ fontSize: 13 }}>Who added and removed</b>
                {d.list_operations.available ? (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>system</th>
                        <th>list</th>
                        <th>op</th>
                        <th>count</th>
                        <th>confirmed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.list_operations.operations.map((r, i) => (
                        <tr key={i}>
                          <td>{r.actor_system}</td>
                          <td>{r.list_name}</td>
                          <td>{r.operation}</td>
                          <td>{fmt.n(r.operations)}</td>
                          <td>{fmt.n(r.confirmed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Empty>
                    No system reports its list operations yet, so this is blank rather than zero —
                    "TextersAI added none today" would be a different and wrong statement.
                  </Empty>
                )}
                <span className="mut" style={{ fontSize: 12 }}>{d.list_operations.note}</span>
              </div>
            </Card>
          </div>

          <div className="tiles">
            <Tile label="inbound (a floor)" value={fmt.n(o.inbound)} />
            <Tile label="transfers" value={fmt.n(o.transfers)} />
            <Tile label="unclassified dispositions" value={fmt.n(o.unclassified)} />
            <Tile label="dials with an agent" value={fmt.n(o.dials_with_an_agent)} />
          </div>
        </>
      )}

      {!d && !error && <div className="mut">Loading…</div>}
    </>
  );
}

/** The pivot. Ammar, 2026-09-30: "i want correlations ,etc like with call nows or wants_call
 *  vs shoved and based on different lists and breakdownse,tc".
 *
 *  Every control here changes a query parameter and nothing else - there is no client-side
 *  aggregation, so what is on screen is always something the backend computed over the whole
 *  range rather than over the rows that happened to be fetched. */
function ExploreCard({
  bd, bdLoading, rows, dims, oursOnly, notOurs, groupBy, setGroupBy, filters, setFilters,
  sortBy, setSortBy, seconds, range, onlyOurDials, setOnlyOurDials,
}: {
  bd: DialingBreakdown | null;
  bdLoading: boolean;
  rows: DialingCell[];
  dims: string[];
  oursOnly: string[];
  notOurs: boolean;
  groupBy: string[];
  setGroupBy: (g: string[]) => void;
  filters: { key: string; value: string }[];
  setFilters: (f: { key: string; value: string }[]) => void;
  sortBy: string;
  setSortBy: (s: string) => void;
  seconds: number;
  range: { since: string; until: string };
  onlyOurDials: boolean | undefined;
  setOnlyOurDials: (b: boolean | undefined) => void;
}) {
  const g0 = groupBy[0] ?? "day";
  const g1 = groupBy[1] ?? "";
  const options = dims.length ? dims : Object.keys(LABELS);
  // The checkbox shows what the QUERY ran with, not what this component asked for. When
  // `onlyOurDials` is undefined the server decided, and a box reading "off" above an ours-only
  // table is how a reader stops believing the page.
  const oursOnlyShown = onlyOurDials ?? bd?.ours_only ?? false;

  const preset = (pz: (typeof PRESETS)[number]) => {
    setGroupBy(pz.group_by);
    setFilters(
      Object.entries(pz.filters ?? {}).map(([key, value]) => ({ key, value })),
    );
  };

  const maxDials = Math.max(1, ...rows.map((r) => Number(r.dials) || 0));

  return (
    <Card title="Explore — group, filter, compare">
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {PRESETS.map((pz) => {
          const on =
            pz.group_by.join(",") === groupBy.join(",") &&
            JSON.stringify(pz.filters ?? {}) ===
              JSON.stringify(Object.fromEntries(filters.filter((f) => f.key && f.value).map((f) => [f.key, f.value])));
          return (
            <button key={pz.label} className={on ? "btn s p" : "btn s"} onClick={() => preset(pz)}>
              {pz.label}
            </button>
          );
        })}
      </div>

      <div className="row" style={{ gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div className="mut" style={{ fontSize: 11, marginBottom: 2 }}>group by</div>
          <select className="f" value={g0} onChange={(e) => setGroupBy([e.target.value, g1].filter(Boolean))}>
            {options.map((x) => (
              <option key={x} value={x}>{label(x)}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="mut" style={{ fontSize: 11, marginBottom: 2 }}>then by (optional)</div>
          <select
            className="f"
            value={g1}
            onChange={(e) => setGroupBy([g0, e.target.value].filter(Boolean))}
          >
            <option value="">—</option>
            {options.filter((x) => x !== g0).map((x) => (
              <option key={x} value={x}>{label(x)}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="mut" style={{ fontSize: 11, marginBottom: 2 }}>sort by</div>
          <select className="f" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>{m.head}</option>
            ))}
            <option value={g0}>{label(g0)} (name)</option>
          </select>
        </div>
        <button
          className="btn s"
          onClick={() => setFilters([...filters, { key: "", value: "" }])}
        >
          + filter
        </button>
        {/* A switch, not a filter: the not-ours bucket is a legitimate value a filter could
            match, so hiding this behind an exact-match string would be a trap. */}
        <label className="row" style={{ gap: 6, alignItems: "center", fontSize: 12.5 }}>
          <input
            type="checkbox"
            checked={oursOnlyShown}
            onChange={(e) => setOnlyOurDials(e.target.checked)}
          />
          only dials our engine placed
          {bd?.ours_only_was_chosen_for_you && (
            <span className="mut" style={{ fontSize: 11 }}>(on by default here)</span>
          )}
        </label>
        {bdLoading && <span className="mut" style={{ fontSize: 12 }}>refreshing…</span>}
      </div>

      {filters.map((f, i) => (
        <FilterRow
          key={i}
          first={i === 0}
          options={options}
          range={range}
          value={f}
          onChange={(next) => setFilters(filters.map((x, j) => (j === i ? next : x)))}
          onRemove={() => setFilters(filters.filter((_, j) => j !== i))}
        />
      ))}

      {/* WHAT IS NOT IN THE TABLE, in numbers, whenever anything is not in the table.
          Ammar's third complaint was the '(not ours)' row swamping every breakdown, and the fix
          is to leave those dials out by default - which would be a worse bug if the page did not
          then say how many and why. `dials_excluded + totals.dials` equals the headline dial
          count above, so the two halves of the page still reconcile by arithmetic a reader can
          do on screen. */}
      {!!bd?.dials_excluded && (
        <div style={{ marginTop: 10 }}>
          <Pill tone="ba">{fmt.n(bd.dials_excluded)} dials not in this table</Pill>
          <span className="mut" style={{ fontSize: 12, marginLeft: 6 }}>
            {bd.excluded_because}{" "}
            <button
              className="btn s"
              style={{ marginLeft: 4 }}
              onClick={() => setOnlyOurDials(false)}
            >
              include them
            </button>
          </span>
        </div>
      )}
      {bd?.cadence_dimensions_are_empty_because && (
        <div style={{ marginTop: 10 }}>
          <Pill tone="ba">one row holds nearly every dial</Pill>
          <span className="mut" style={{ fontSize: 12, marginLeft: 6 }}>
            {bd.cadence_dimensions_are_empty_because}{" "}
            <button
              className="btn s"
              style={{ marginLeft: 4 }}
              onClick={() => setOnlyOurDials(true)}
            >
              show only our dials
            </button>
          </span>
        </div>
      )}
      {notOurs && !bd?.cadence_dimensions_are_empty_because && !bd?.dials_excluded && (
        <div style={{ marginTop: 10 }}>
          <Pill tone="ba">only known for dials this engine placed</Pill>
          <span className="mut" style={{ fontSize: 12, marginLeft: 6 }}>
            Every other dial lands in <b>{bd?.not_ours_label ?? "(another system placed it)"}</b>{" "}
            rather than being dropped, so the total still matches the dial count above.
          </span>
        </div>
      )}

      {bd && (
        <div className="mut" style={{ fontSize: 12, margin: "10px 0 4px" }}>
          {fmt.n(bd.totals.dials)} dials · {fmt.n(bd.totals.connected)} connected at{" "}
          {seconds === 0 ? "any length" : `${seconds}s`} · {fmt.n(bd.totals.answered)} answered ·{" "}
          {/* `groups_total` is the number of buckets the QUERY found; `rows` is what fits in the
              server's row cap. They differed on `our_number` (216 against 200) and the page said
              only the smaller one, so a reader sorting a truncated table had no way to know the
              tail was missing. */}
          {bd.truncated
            ? `showing the top ${bd.rows.length} of ${fmt.n(bd.groups_total ?? bd.rows.length)} groups — the rest are in the totals but not listed`
            : `${bd.rows.length} ${bd.rows.length === 1 ? "group" : "groups"}`}
          {bd.ours_only ? " · only dials our engine placed" : ""}
        </div>
      )}

      {rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{label(g0)}</th>
                {g1 && <th>{label(g1)}</th>}
                <th />
                {METRICS.map((m) => (
                  <th
                    key={m.key}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSortBy(m.key)}
                    title="sort by this"
                  >
                    {m.head}
                    {sortBy === m.key ? " ↓" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><b>{String(r[g0] ?? "—")}</b></td>
                  {g1 && <td>{String(r[g1] ?? "—")}</td>}
                  <td><Bar value={Number(r.dials) || 0} of={maxDials} /></td>
                  {METRICS.map((m) => (
                    <td key={m.key}>{metric(r, m.key, m.kind)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>
          {bd
            ? "No dials match. Widen the range or clear a filter."
            : "Loading…"}
        </Empty>
      )}

      <span className="mut" style={{ fontSize: 12 }}>
        <b>connected</b> is a dial lasting at least {seconds}s. <b>answered</b> is a dial with an
        agent on the record. On production they differ by roughly a factor of two, so both are
        always shown and neither is called "the" connect rate.
      </span>
    </Card>
  );
}

/** One `where <dimension> is <value>` line, with the values FETCHED rather than typed.
 *
 *  Ammar: "when i select different groups and by then filters,etc they don,t just work". Every
 *  filter did reach the SQL - verified against production for all twenty dimensions - but the
 *  value was a free-text box compared with `=` against the dimension's own expression. So
 *  `closers` matched 2,088 dials and `Closers` matched none; `wants_call` worked and "call now"
 *  did not; `(nobody answered)` was unguessable. A wrong guess rendered an empty table, which on
 *  this page is indistinguishable from "this happened zero times" - the filter looked broken
 *  because the only feedback it gave was the same as a true zero.
 *
 *  So the values come from `/dialing/values`, which produces them with the same SQL expression
 *  the filter is matched against, over the same date range. Anything listed is guaranteed to
 *  match. The free-text box stays as an escape hatch for a value that has dropped out of the
 *  range, and says so rather than pretending the list is exhaustive. */
function FilterRow({
  first, options, range, value, onChange, onRemove,
}: {
  first: boolean;
  options: string[];
  range: { since: string; until: string };
  value: { key: string; value: string };
  onChange: (v: { key: string; value: string }) => void;
  onRemove: () => void;
}) {
  const [choices, setChoices] = useState<DialingValues | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!value.key) {
      setChoices(null);
      return;
    }
    let live = true;
    setChoices(null);
    setFailed(false);
    api
      .dialingValues({ dimension: value.key, ...range })
      .then((r) => live && setChoices(r))
      // A failure must not leave an empty dropdown looking like "this dimension has no values" -
      // that is the same lie in a different place. Fall back to the text box and say so.
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [value.key, range.since, range.until]);

  const known = choices?.values ?? [];
  const listed = known.some((v) => v.value === value.value);

  return (
    <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
      <span className="mut" style={{ fontSize: 12, width: 34 }}>{first ? "where" : "and"}</span>
      <select
        className="f"
        value={value.key}
        onChange={(e) => onChange({ key: e.target.value, value: "" })}
      >
        <option value="">choose…</option>
        {options.map((x) => (
          <option key={x} value={x}>{label(x)}</option>
        ))}
      </select>
      <span className="mut">is</span>
      {known.length > 0 ? (
        <select
          className="f"
          value={listed ? value.value : ""}
          style={{ maxWidth: 320 }}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        >
          <option value="">any</option>
          {known.map((v) => (
            <option key={v.value} value={v.value}>
              {v.value} ({fmt.n(v.dials)})
            </option>
          ))}
        </select>
      ) : (
        <input
          className="f"
          placeholder={value.key ? "exact value" : "pick a dimension first"}
          value={value.value}
          style={{ width: 200 }}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
      )}
      <button className="btn s d" onClick={onRemove}>remove</button>
      {failed && (
        <span className="mut" style={{ fontSize: 12 }}>
          could not list this dimension's values — type one exactly
        </span>
      )}
      {!failed && value.key && !choices && (
        <span className="mut" style={{ fontSize: 12 }}>loading values…</span>
      )}
      {choices && value.value && !listed && (
        <span className="mut" style={{ fontSize: 12 }}>
          “{value.value}” does not occur in this range — it will match nothing
        </span>
      )}
      {choices?.truncated && (
        <span className="mut" style={{ fontSize: 12 }}>
          top {known.length} of {fmt.n(choices.total_values)} values
        </span>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", padding: "3px 0" }}>
      <span className="mut">{label}</span>
      <b>{value}</b>
    </div>
  );
}
